import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';

import { CURRENCY_OPTIONS, PAYMENT_METHOD_OPTIONS } from '../../constants';
import { formatCurrency } from '../../utils/CommonUtils';

/**
 * Редактор рядків оплати: валюта + сума + спосіб оплати.
 *
 * Один компонент замість трьох майже однакових копій у NewSale,
 * OpenSales і SaleReturn. Кожен рядок — це { currency, sum, paymentMethod },
 * де paymentMethod уже містить код бекенду (CASH / CARD / IBAN).
 */
const PaymentEditor = ({ payments = [], onChange, errors = [] }) => {
    const update = (index, patch) => {
        const next = payments.map((payment, i) =>
            i === index ? { ...payment, ...patch } : payment
        );
        onChange(next);
    };

    const remove = (index) => {
        onChange(payments.filter((_, i) => i !== index));
    };

    const add = () => {
        onChange([...payments, { currency: 'UAH', sum: 0, paymentMethod: null }]);
    };

    return (
        <div>
            {payments.length === 0 && (
                <Message
                    className="rc-message"
                    severity="warn"
                    text="Додайте валюту та спосіб оплати"
                />
            )}

            {payments.map((payment, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className="rc-payment-row" key={`payment-${index}`}>
                    <div className="rc-payment-row__top">
                        <div className="rc-payment-row__currency">
                            <Dropdown
                                value={payment.currency}
                                options={CURRENCY_OPTIONS}
                                onChange={(e) => update(index, { currency: e.value })}
                                aria-label="Валюта оплати"
                            />
                        </div>
                        <div className="rc-payment-row__amount">
                            <InputNumber
                                value={Number(payment.sum) || 0}
                                onValueChange={(e) => update(index, { sum: e.value ?? 0 })}
                                mode="decimal"
                                locale="uk-UA"
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                inputMode="decimal"
                                aria-label="Сума оплати"
                            />
                        </div>
                        <Button
                            type="button"
                            icon="pi pi-trash"
                            severity="danger"
                            text
                            rounded
                            aria-label="Видалити рядок оплати"
                            onClick={() => remove(index)}
                        />
                    </div>
                    <div className="rc-payment-row__method">
                        <Dropdown
                            value={payment.paymentMethod}
                            options={PAYMENT_METHOD_OPTIONS}
                            onChange={(e) => update(index, { paymentMethod: e.value })}
                            placeholder="Спосіб оплати"
                            className={errors[index]?.paymentMethod ? 'p-invalid' : ''}
                            aria-label="Спосіб оплати"
                        />
                        {errors[index]?.paymentMethod && (
                            <small className="rc-field-error">{errors[index].paymentMethod}</small>
                        )}
                    </div>
                </div>
            ))}

            <Button
                type="button"
                label="Додати валюту"
                icon="pi pi-plus"
                severity="secondary"
                outlined
                className="w-full mt-3"
                onClick={add}
            />
        </div>
    );
};

/**
 * Підказка про залишок, який ще не розподілений між способами оплати.
 * Заокруглюємо до копійок, щоб похибка обчислень з плаваючою комою
 * не показувала касиру «Нерозподілена сума: 0.0000000001 грн».
 */
export const UndistributedMessage = ({ amount }) => {
    const rounded = Math.round((Number(amount) || 0) * 100) / 100;
    const settled = Math.abs(rounded) < 0.01;

    return (
        <Message
            className="rc-message"
            severity={settled ? 'success' : rounded > 0 ? 'warn' : 'error'}
            text={
                settled
                    ? 'Суму розподілено повністю'
                    : rounded > 0
                      ? `Залишилось розподілити: ${formatCurrency(rounded, 'UAH')}`
                      : `Перевищення суми на ${formatCurrency(Math.abs(rounded), 'UAH')}`
            }
        />
    );
};

export default PaymentEditor;
